import type { UserProfileDto } from "@/types/user";

import useSWR from "swr";
import { CONFIG } from "@/config-global";
import axios, { fetcher, endpoints } from "@/utils/axios";

// ----------------------------------------------------------------------
const USER_ENDPOINT = `${CONFIG.serverUrl}${endpoints.user}`;

const ENDPOINTS = {
  getUserProfile: "get-user-profile",
  updateUserProfile: "update-user-profile",
  registerUser: "register-user",
};

const swrOptions = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};

// ----------------------------------------------------------------------

export function useGetUserProfile(userId: string) {
  const url = userId
    ? [
        `${USER_ENDPOINT}`,
        { params: { id: userId, endpoint: ENDPOINTS.getUserProfile } },
      ]
    : null;

  const { data, error, isLoading, isValidating } = useSWR<{
    user: UserProfileDto;
  }>(url, fetcher, swrOptions);

  return {
    user: data?.user,
    userLoading: isLoading,
    userError: error,
    userValidating: isValidating,
  };
}

// ----------------------------------------------------------------------

export async function registerUser(userData: {
  email: string;
  id: string;
  firstName: string;
  lastName: string;
  profilePictureUrl?: string;
}) {
  const params = { endpoint: ENDPOINTS.registerUser };
  const url = `${CONFIG.serverUrl}${endpoints.register}`;

  const response = await axios.post(
    url,
    { registerData: userData },
    { params }
  );
  return response.data;
}

// ----------------------------------------------------------------------

export async function updateUserProfile(
  userId: string,
  profileData: {
    name?: string;
    firstName?: string;
    lastName?: string;
    notificationPreferences?: {
      chatMessages?: boolean;
      changeRequests?: boolean;
      teamUpdates?: boolean;
      meetingReminders?: boolean;
    };
  }
) {
  const params = { endpoint: ENDPOINTS.updateUserProfile, id: userId };
  const url = `${USER_ENDPOINT}`;

  const response = await axios.patch(url, profileData, { params });
  return response.data;
}
